import { Box, CheckboxInput, Spinner } from "@sembark-travel/ui/base"
import { useXHR } from "@sembark-travel/xhr"
import useSWR from "swr"
import { IBooking, IBookingDetail } from "./store"
import {
	formatDate,
	getDiff,
	localOrUtcTimestampToLocalDate,
} from "@sembark-travel/datetime-utils"
import {
	addMoneyFromDifferentCurrencies,
	formatMoneyByIntl,
	moneyParseByDecimal,
} from "@sembark-travel/money"
import { ShareViaWhatsApp } from "@sembark-travel/ui/share"
import pluralize from "pluralize"
import { withOrdinalSuffix } from "@sembark-travel/number-utils"
import { useSynedStorageState } from "../storage"
import { TTenantBrand, TTenantBrandAddress } from "../Brands/store"

type TWhatsappInclusionOptions = {
	price: boolean
	billing: boolean
	check_ins: boolean
}
type THotelBooking = {
	hotel_bookings: IBooking[]
	billing_address: TTenantBrandAddress
	tenant_brand: TTenantBrand
}
export function ShareHotelBookingEnquiry({
	bookingDetails,
}: {
	bookingDetails: IBooking
}) {
	const xhr = useXHR()
	const { id: hotelBookingId } = bookingDetails
	const baseKey = `hotel_booking_whatsapp_content_${hotelBookingId}`
	const [state, setState] = useSynedStorageState<TWhatsappInclusionOptions>(
		baseKey,
		{
			price: true,
			billing: true,
			check_ins: false,
		}
	)
	const { data: booking } = useSWR<THotelBooking>(
		[`/hotel-bookings/${hotelBookingId}/as-whatsapp`, bookingDetails],
		() =>
			xhr
				.get(`/hotel-bookings/${hotelBookingId}/as-whatsapp`, {
					params: { can_view_prices: 1 },
				})
				.then((resp) => resp.data.data)
	)
	if (!booking) {
		return <Spinner padding="4" alignCenter />
	}
	const text = getWhatsAppMessageContentForBooking(
		booking,
		state,
		hotelBookingId
	)

	return (
		<Box position={"relative"}>
			<Box
				position="sticky"
				top="0"
				padding="4"
				zIndex="10"
				bgColor="subtle"
				borderBottomWidth="1"
				boxShadow="sm"
			>
				<Box display="flex" flexWrap="wrap" gap="4">
					<Box as="label">
						<CheckboxInput
							checked={state.price}
							onChange={() =>
								setState({
									...state,
									price: !state.price,
								})
							}
						/>{" "}
						Price
					</Box>
					<Box as="label">
						<CheckboxInput
							checked={state.billing}
							onChange={() =>
								setState({
									...state,
									billing: !state.billing,
								})
							}
						/>{" "}
						Billing
					</Box>
					{booking.hotel_bookings.length > 1 ? (
						<Box as="label">
							<CheckboxInput
								checked={state.check_ins}
								onChange={() =>
									setState({
										...state,
										check_ins: !state.check_ins,
									})
								}
							/>{" "}
							All Check-ins
						</Box>
					) : null}
				</Box>
			</Box>
			<Box>
				<ShareViaWhatsApp
					text={text}
					phoneNumber={undefined}
					trackingContentType={"hotel_bookings"}
					trackingContentId={"booking_enquiry"}
				/>
			</Box>
		</Box>
	)
}
interface GroupedBooking {
	dates: string[]
	detail: IBookingDetail
}

function getWhatsAppMessageContentForBooking(
	hotelBookings: THotelBooking,
	state: TWhatsappInclusionOptions,
	hotelBookingId: string | number
) {
	const { hotel_bookings, billing_address, tenant_brand } = hotelBookings
	const allHotelBookings = !state.check_ins
		? hotel_bookings.filter((hotel) => hotel.id === Number(hotelBookingId))
		: hotel_bookings
	const hotelDetails = allHotelBookings[0]

	// billing details will be in state
	const { price: showPricing, billing: showBilling } = state
	const totalPrice = addMoneyFromDifferentCurrencies(
		allHotelBookings.map(({ booked_price, currency }) =>
			moneyParseByDecimal(booked_price || 0, currency)
		)
	)
	const brandName = tenant_brand.name.includes(tenant_brand.short_name)
		? tenant_brand.short_name
		: `${tenant_brand.short_name} (${tenant_brand.name})`
	const bookingText = `
Dear Sir/Madam,  
Greetings from *${brandName}*  

We are pleased to share the below booking.Would request you to kindly *BOOK & CONFIRM* the same.  

*🆔 Trip ID:* ${hotelDetails.trip_id}  

🏨 *Hotel:* ${hotelDetails.hotel.name}, ${hotelDetails.hotel.location.short_name}  
${
	hotelDetails.trip
		? `👤 *Guest:* ${hotelDetails.trip.tourist?.name ? hotelDetails.trip.tourist?.name : "Name - To be updated"}  
👥 *No. of Guests:* ${hotelDetails.pax_string}`
		: ""
}  
${allHotelBookings
	.map((booking, index) => {
		const nightsRelativeTo: Date = localOrUtcTimestampToLocalDate(
			booking.checkin_local,
			booking.checkin
		)
		const groups: Record<string, GroupedBooking> = {}

		booking.details?.forEach((item) => {
			const key = [
				item.room_type.id,
				item.meal_plan.id,
				item.adults_with_extra_bed,
				item.children_with_extra_bed,
				item.children_without_extra_bed,
				item.per_adult_with_extra_bed_booked_price ?? "",
				item.per_child_without_extra_bed_booked_price ?? "",
				item.per_child_with_extra_bed_booked_price ?? "",
				item.per_room_booked_price,
				item.booked_price,
			].join("|")

			if (!groups[key]) {
				groups[key] = {
					dates: [],
					detail: item, // keep full IBookingDetail object
				}
			}

			groups[key].dates.push(item.date)
		})

		const uniqueDetailsDateWise = Object.values(groups)

		return `${index > 0 ? `🔁 *The guest will re-check-in with the following details.*\n` : ""}
🗓️ *Check-in:* ${formatDate(localOrUtcTimestampToLocalDate(booking.overall_checkin_local, booking.overall_checkin), "DD MMM, YYYY")}  
🗓️ *Check-out:* ${formatDate(localOrUtcTimestampToLocalDate(booking.overall_checkout_local, booking.overall_checkout), "DD MMM, YYYY")}  
${booking.no_of_nights > 1 ? `🌙 *No. of Nights:* (${pluralize("Night", booking.no_of_nights, true)})\n` : ""} 
${
	uniqueDetailsDateWise.length > 1
		? uniqueDetailsDateWise
				.map(({ dates, detail }) => {
					return `${dates
						.map((date) => {
							const night = getDiff(date, nightsRelativeTo, "days") + 1
							return `🌙 *${withOrdinalSuffix(night)} Night*`
						})
						.join(",")}
${detailsString(detail, state, dates.length)}`
				})
				.join("\n")
		: booking.details?.[0]
			? `${detailsString(booking.details[0], state, booking.no_of_nights)} `
			: ""
}${
			(booking.extras?.length || 0) > 0
				? `*Other Inclusions:*  
${booking.extras
	?.map((ex) => {
		const night = ex.date
			? getDiff(
					localOrUtcTimestampToLocalDate(ex.date_local, ex.date),
					nightsRelativeTo,
					"days"
				) + 1
			: 0
		return `  • ${withOrdinalSuffix(night)} Night - ${ex.service.name} ${
			showPricing
				? `: ${formatMoneyByIntl(
						moneyParseByDecimal(ex.booked_price || 0, ex.currency),
						{ showCurrency: true }
					)}`
				: ""
		} `
	})
	.join("\n")}`
				: ""
		} `
	})
	.join("\n")}`
	const totalPriceText = showPricing
		? `
💰 *Total Booking Price:* ${totalPrice
				.map((p) => formatMoneyByIntl(p, { showCurrency: true }))
				.join(" + ")}`
		: ""

	const billingDetailsText =
		showBilling && billing_address
			? `${showPricing ? "\n" : ""}*Payment Mode:* 
Bill to ${billing_address.addressing_name}. All Extras on Direct Basis.

🧾*Billing Instructions*

*Name:* ${billing_address.addressing_name} 
*Address:* ${billing_address.line_1}${billing_address.line_2 ? `${billing_address.line_2}` : ""}${billing_address.location.city ? `, ${billing_address.location.city.name}` : ""}${billing_address.location.state ? `, ${billing_address.location.state.name}` : ""}${billing_address.location.country ? `, ${billing_address.location.country.name}` : ""}-${billing_address.postcode}
*Details:* ${billing_address.billing_details}
`
			: ""

	return [bookingText, totalPriceText, billingDetailsText]
		.filter(Boolean)
		.join("\n")
}

function detailsString(
	detail: IBookingDetail,
	state: TWhatsappInclusionOptions,
	no_of_nights: number
) {
	// billing details will be in state
	const { price: showPricing } = state
	const roomDetails = [
		`  • ${detail.no_of_rooms} ${detail.room_type.name} ${
			showPricing
				? ` X ${formatMoneyByIntl(
						moneyParseByDecimal(
							detail.per_room_booked_price || 0,
							detail.currency
						)
					)}`
				: ""
		}`,
		detail.adults_with_extra_bed > 0
			? `  • ${detail.adults_with_extra_bed} ${pluralize("Adult", detail.adults_with_extra_bed)} with Extra Bed/Mattress ${
					showPricing
						? ` X ${formatMoneyByIntl(
								moneyParseByDecimal(
									detail.per_adult_with_extra_bed_booked_price || 0,
									detail.currency
								)
							)}`
						: ""
				}`
			: null,
		detail.children_with_extra_bed > 0
			? `  • ${detail.children_with_extra_bed} ${pluralize("Child", detail.children_with_extra_bed)} with Extra Bed/Mattress ${
					showPricing
						? ` X ${formatMoneyByIntl(
								moneyParseByDecimal(
									detail.per_child_with_extra_bed_booked_price || 0,
									detail.currency
								)
							)}`
						: ""
				}`
			: null,
		detail.children_without_extra_bed > 0
			? `  • ${detail.children_without_extra_bed} ${pluralize("Child", detail.children_without_extra_bed)} without Extra Bed/Mattress ${
					showPricing
						? ` X ${formatMoneyByIntl(
								moneyParseByDecimal(
									detail.per_child_without_extra_bed_booked_price || 0,
									detail.currency
								)
							)}`
						: ""
				}`
			: null,
	].filter(Boolean)

	return `🍽 *Meal Plan:* ${detail.meal_plan.name}
🏠 *Room Details & Rates:*
${roomDetails.join("\n")} 
   ${
			showPricing
				? `*Total:* ${formatMoneyByIntl(
						moneyParseByDecimal(detail.booked_price || 0, detail.currency)
					)} X ${no_of_nights}N = ${detail.currency} ${formatMoneyByIntl(
						moneyParseByDecimal(
							(detail.booked_price || 0) * no_of_nights,
							detail.currency
						)
					)}`
				: ""
		}`
}
